import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux factures de vente
 */
class SaleInvoiceService {
    /**
     * Récupérer la liste des factures avec filtres et pagination
     * @param {Object} params Paramètres de filtrage et pagination
     * @param {String} [params.search] Terme de recherche (numéro de facture, nom/téléphone client)
     * @param {Number} [params.per_page] Nombre d'éléments par page (défaut: 15, min:1, max:100)
     * @param {Number} [params.page] Numéro de page (min:1)
     * @param {String} [params.status] Statut de la facture (pending, partial, paid)
     * @param {Number} [params.customer_id] ID du client
     * @param {String} [params.date_from] Date de début (format date)
     * @param {String} [params.date_to] Date de fin (format date)
     * @param {String} [params.sort_by] Champ de tri (invoice_number, total_amount, paid_amount, status, created_at, due_date)
     * @param {String} [params.sort_direction] Direction du tri (asc, desc)
     * @returns {Promise} Réponse de l'API avec la liste des factures
     */
    getSaleInvoices(params = {}) {
        return ApiService.get('/sale-invoices', params);
    }

    /**
     * Récupérer une facture par son ID
     * @param {Number} id ID de la facture
     * @returns {Promise} Réponse de l'API avec les détails de la facture
     */
    getSaleInvoice(id) {
        return ApiService.get(`/sale-invoices/${id}`);
    }

    /**
     * Créer une nouvelle facture en regroupant plusieurs ventes différées
     * @param {Object} invoiceData Données de la facture
     * @param {Number} invoiceData.customer_id ID du client (requis)
     * @param {Array<Number>} invoiceData.sale_ids Liste des IDs des ventes à facturer (requis, min:1)
     * @param {String} [invoiceData.due_date] Date d'échéance (format date)
     * @param {String} [invoiceData.notes] Notes (max: 1000 caractères)
     * @returns {Promise} Réponse de l'API avec la facture créée
     */
    createSaleInvoice(invoiceData) {
        return ApiService.post('/sale-invoices', invoiceData);
    }

    /**
     * Mettre à jour une facture existante
     * @param {Number} id ID de la facture
     * @param {Object} invoiceData Nouvelles données de la facture
     * @param {String} [invoiceData.due_date] Date d'échéance (format date)
     * @param {String} [invoiceData.notes] Notes (max: 1000 caractères)
     * @returns {Promise} Réponse de l'API avec la facture mise à jour
     */
    updateSaleInvoice(id, invoiceData) {
        return ApiService.put(`/sale-invoices/${id}`, invoiceData);
    }

    /**
     * Supprimer une facture (uniquement si aucun paiement n'a été effectué)
     * @param {Number} id ID de la facture
     * @returns {Promise} Réponse de l'API
     * @throws {Error} Si la facture a des paiements
     */
    deleteSaleInvoice(id) {
        return ApiService.delete(`/sale-invoices/${id}`);
    }

    /**
     * Ajouter une vente à une facture existante
     * @param {Number} id ID de la facture
     * @param {Number} saleId ID de la vente à ajouter
     * @returns {Promise} Réponse de l'API avec la facture mise à jour
     */
    addSaleToInvoice(id, saleId) {
        return ApiService.post(`/sale-invoices/${id}/add-sale`, {
            sale_id: saleId
        });
    }

    /**
     * Retirer une vente d'une facture
     * @param {Number} id ID de la facture
     * @param {Number} saleId ID de la vente à retirer
     * @returns {Promise} Réponse de l'API avec la facture mise à jour
     * @throws {Error} Si c'est la dernière vente de la facture
     */
    removeSaleFromInvoice(id, saleId) {
        return ApiService.delete(`/sale-invoices/${id}/sales/${saleId}`);
    }

    /**
     * Ajouter un paiement à une facture
     * @param {Number} id ID de la facture
     * @param {Object} paymentData Données du paiement
     * @param {Number} paymentData.amount Montant du paiement (requis, min: 0.01)
     * @param {String} paymentData.payment_type Type de paiement (requis: cash, card, mobile_money, bank_transfer, check)
     * @param {String} paymentData.payment_date Date du paiement (requis, format date)
     * @param {Number} [paymentData.cash_register_id] ID de la caisse enregistreuse
     * @param {String} [paymentData.notes] Notes (max: 500 caractères)
     * @returns {Promise} Réponse de l'API avec la facture et le paiement créés
     * @throws {Error} Si le montant dépasse le solde restant
     */
    addPaymentToInvoice(id, paymentData) {
        return ApiService.post(`/sale-invoices/${id}/payments`, paymentData);
    }

    /**
     * Récupérer la liste des paiements d'une facture
     * @param {Number} id ID de la facture
     * @returns {Promise} Réponse de l'API avec la liste des paiements
     */
    getInvoicePayments(id) {
        return ApiService.get(`/sale-invoices/${id}/payments`);
    }

    /**
     * Supprimer un paiement d'une facture
     * @param {Number} id ID de la facture
     * @param {Number} paymentId ID du paiement à supprimer
     * @returns {Promise} Réponse de l'API avec la facture mise à jour
     */
    deleteInvoicePayment(id, paymentId) {
        return ApiService.delete(`/sale-invoices/${id}/payments/${paymentId}`);
    }

    /**
     * Récupérer les ventes différées non facturées d'un client
     * @param {Number} customerId ID du client
     * @returns {Promise} Réponse de l'API avec la liste des ventes non facturées et le montant total
     */
    getUninvoicedSales(customerId) {
        return ApiService.get(`/sale-invoices/uninvoiced-sales/${customerId}`);
    }

    /**
     * Récupérer les statistiques des factures
     * @returns {Promise} Réponse de l'API avec les statistiques (total_invoices, pending_invoices, partial_invoices, paid_invoices, total_amount, total_paid, total_remaining)
     */
    getStats() {
        return ApiService.get('/sale-invoices/stats');
    }

    /**
     * Télécharger une facture en PDF
     * @param {Number} invoiceId ID de la facture
     * @returns {Promise} Télécharge le PDF de la facture
     */
    async downloadInvoice(invoiceId) {
        // Import dynamique de html2pdf pour éviter les problèmes de chargement
        const html2pdf = (await import('html2pdf.js')).default;
        
        const response = await ApiService.get(`/sale-invoices/${invoiceId}/pdf-data`);
        const { html, filename } = response.data.data;
        
        // Créer un élément temporaire
        const element = document.createElement('div');
        element.innerHTML = html;
        document.body.appendChild(element);
        
        // Générer le PDF
        await html2pdf()
            .set({
                margin: 1,
                filename: filename,
                image: { type: 'jpeg', quality: 3 },
                html2canvas: { scale: 3 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            })
            .from(element)
            .save();
        
        // Nettoyer
        document.body.removeChild(element);
    }


    /**
     * Télécharger une facture en PDF
     * @param {Number} invoiceId ID de la facture
     * @returns {Promise} Télécharge le PDF de la facture
     */
    async downloadInvoiceDetailed(invoiceId) {
        // Import dynamique de html2pdf pour éviter les problèmes de chargement
        const html2pdf = (await import('html2pdf.js')).default;
        
        const response = await ApiService.get(`/sale-invoices/${invoiceId}/pdf-data-detailed`);
        const { html, filename } = response.data.data;
        
        // Créer un élément temporaire
        const element = document.createElement('div');
        element.innerHTML = html;
        document.body.appendChild(element);
        
        // Générer le PDF
        await html2pdf()
            .set({
                margin: 1,
                filename: filename,
                image: { type: 'jpeg', quality: 3 },
                html2canvas: { scale: 3 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            })
            .from(element)
            .save();
        
        // Nettoyer
        document.body.removeChild(element);
    }

    getStatusLabel(status) {
        const labels = {
            pending: 'En attente',
            partial: 'Partiellement payée',
            paid: 'Payée',
            overdue: 'En retard',
            cancelled: 'Annulée'
        }
        return labels[status] || status
    }

    getStatusClasses(status) {
        const classes = {
            pending: 'bg-orange-50 text-orange-700',
            partial: 'bg-yellow-50 text-yellow-700',
            paid: 'bg-green-50 text-green-700',
            overdue: 'bg-red-50 text-red-700',
            cancelled: 'bg-gray-100 text-gray-700'
        }
        return classes[status] || 'bg-gray-100 text-gray-700'
    }

    getStatusDotClasses(status) {
        const dots = {
            pending: 'bg-orange-500',
            partial: 'bg-yellow-500',
            paid: 'bg-green-500',
            overdue: 'bg-red-500',
            cancelled: 'bg-gray-500'
        }
        return dots[status] || 'bg-gray-500'
    }

    formatDate(dateStr) {
        if (!dateStr) return '-'
        return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }

    formatCurrency(amount) {
        if (amount === null || amount === undefined) return '-'
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(Number(amount))
    }

}

export default new SaleInvoiceService();
